import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Server } from "lucide-react";
import { api, ApiError } from "../lib/api";
import type { SettingsResponse } from "../lib/types";
import { dateTime, number } from "../lib/format";
import { useEffect, useState } from "react";

export default function Settings() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsResponse>("/superadmin/settings"),
    refetchInterval: 60_000,
  });

  const update = useMutation({
    mutationFn: (values: Record<string, boolean | number | string>) =>
      api.patch("/superadmin/settings", values),
    onSuccess: () => { setError(null); queryClient.invalidateQueries({ queryKey: ["settings"] }); },
    // At least one payment method must stay on — the model refuses otherwise.
    onError: (err) => setError(err instanceof ApiError ? err.message : "Kunde inte spara"),
  });

  if (isLoading || !data) return <div className="panel h-96 animate-pulse" />;

  const queue = data.system.queue;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-white">Inställningar</h1>
        <p className="text-sm text-ink-500">Plattformsomfattande reglage och systemhälsa</p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="panel p-4">
          <h2 className="mb-1 text-sm font-semibold text-white">Betalmetoder</h2>
          <p className="mb-3 text-xs text-ink-500">
            Styr vad kunden kan välja i appen. Minst en måste vara på.
          </p>
          <div className="space-y-2">
            <Toggle
              label="Klarna" description="Betalning i appen via Stripe/Klarna"
              enabled={data.payment_methods.klarna_enabled}
              busy={update.isPending}
              onChange={(v) => update.mutate({ klarna_enabled: v })}
            />
            <Toggle
              label="Betala på plats" description="Kunden betalar verkstaden direkt; vi fakturerar avgiften"
              enabled={data.payment_methods.pay_at_garage_enabled}
              busy={update.isPending}
              onChange={(v) => update.mutate({ pay_at_garage_enabled: v })}
            />
          </div>
        </div>

        <div className="panel p-4">
          <h2 className="mb-3 text-sm font-semibold text-white">Varumärke (från miljövariabler)</h2>
          <dl className="space-y-1.5 text-sm">
            {Object.entries(data.brand).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-3">
                <dt className="text-ink-500">{BRAND_LABELS[key] || key}</dt>
                <dd className="truncate text-ink-200">{String(value)}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-ink-600">
            Ändras per klient i Kamal-överlägget, inte här — en klient = ett deploy-mål.
          </p>
        </div>
      </div>

      <div className="panel p-4">
        <h2 className="mb-1 text-sm font-semibold text-white">Bokning</h2>
        <p className="mb-3 text-xs text-ink-500">
          Gäller alla företag som inte satt ett eget bokningsfönster — till skillnad
          från avtalsvillkoren nedan slår en ändring här igenom direkt.
        </p>
        <div className="grid gap-3 sm:grid-cols-5">
          <Field
            label="Bokningsfönster (dagar)" type="number" step="1"
            value={data.booking_defaults.horizon_days}
            busy={update.isPending}
            onSave={(v) => update.mutate({ default_booking_horizon_days: v })}
          />
        </div>
      </div>

      <div className="panel p-4">
        <h2 className="mb-1 text-sm font-semibold text-white">Avtalsvillkor för nya företag</h2>
        <p className="mb-3 text-xs text-ink-500">
          Kopieras till verkstaden eller centret när det läggs upp. Att ändra här
          rör inte befintliga företag — deras villkor ändras på deras egen sida.
        </p>
        <div className="grid gap-3 sm:grid-cols-5">
          <Field
            label="Avgift %" type="number" step="0.01"
            value={data.billing_defaults.fee_percent}
            busy={update.isPending}
            onSave={(v) => update.mutate({ default_fee_percent: v })}
          />
          <Field
            label="Fast avgift" type="number" step="1"
            value={data.billing_defaults.fee_fixed}
            busy={update.isPending}
            onSave={(v) => update.mutate({ default_fee_fixed: v })}
          />
          <Field
            label="Månadsavgift" type="number" step="1"
            value={data.billing_defaults.subscription_fee}
            busy={update.isPending}
            onSave={(v) => update.mutate({ default_subscription_fee: v })}
          />
          <div>
            <label className="mb-1 block text-xs text-ink-500">Period</label>
            <select
              className="field w-full py-1.5 text-sm"
              value={data.billing_defaults.billing_period}
              disabled={update.isPending}
              onChange={(e) => update.mutate({ default_billing_period: e.target.value })}
            >
              {data.billing_defaults.periods.map((p) => (
                <option key={p} value={p}>{PERIOD_LABELS[p] || p}</option>
              ))}
            </select>
          </div>
          <Field
            label="Betalningsvillkor (dagar)" type="number" step="1"
            value={data.billing_defaults.payment_terms_days}
            busy={update.isPending}
            onSave={(v) => update.mutate({ default_payment_terms_days: v })}
          />
        </div>
      </div>

      <div className="panel p-4">
        <div className="mb-3 flex items-center gap-2">
          <Server className="h-4 w-4 text-ink-400" />
          <h2 className="text-sm font-semibold text-white">System</h2>
        </div>

        {data.system.error && <p className="text-sm text-red-300">{data.system.error}</p>}

        {queue && (
          <>
            <div className="grid gap-2.5 sm:grid-cols-4">
              <Stat label="Misslyckade jobb" value={queue.failed} tone={queue.failed > 0 ? "danger" : "ok"} />
              <Stat label="Väntande jobb" value={queue.pending} tone={queue.pending > 50 ? "warning" : "ok"} />
              <Stat label="Schemalagda" value={queue.scheduled} tone="ok" />
              <Stat label="Workers" value={queue.workers} tone={queue.workers === 0 ? "danger" : "ok"} />
            </div>

            {queue.oldest_pending_at && (
              <p className="mt-2 text-xs text-ink-500">
                Äldsta väntande jobb köades {dateTime(queue.oldest_pending_at)}.
              </p>
            )}

            {queue.recent_failures.length > 0 && (
              <div className="mt-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
                  Senaste fel
                </h3>
                <div className="space-y-1.5">
                  {queue.recent_failures.map((failure) => (
                    <div key={failure.id} className="rounded border border-ink-800 bg-ink-850/50 p-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="text-xs font-medium text-ink-200">{failure.job || "Okänt jobb"}</span>
                        <span className="num text-[10px] text-ink-600">{dateTime(failure.failed_at)}</span>
                      </div>
                      <p className="mt-0.5 break-words text-[11px]">
                        {failure.error_class && (
                          <span className="font-medium text-red-400">{failure.error_class}: </span>
                        )}
                        <span className="text-red-300/80">{failure.error}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <dl className="mt-4 grid gap-x-10 gap-y-1.5 border-t border-ink-850 pt-3 text-xs sm:grid-cols-2">
          <Row label="Miljö" value={data.system.rails_env} />
          <Row label="Databas" value={data.system.database?.name} />
          <Row label="Tidszon" value={data.system.time_zone} />
          <Row label="Servertid" value={data.system.server_time ? dateTime(data.system.server_time) : undefined} />
        </dl>
      </div>
    </div>
  );
}

const PERIOD_LABELS: Record<string, string> = { monthly: "Varje månad", semimonthly: "Två gånger i månaden" };

// Saves on blur or Enter rather than on every keystroke, so a half-typed rate
// is never what gets written.
function Field({ label, value, type, step, busy, onSave }: {
  label: string; value: number; type: string; step: string; busy: boolean;
  onSave: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = () => {
    const next = Number(draft);
    if (Number.isFinite(next) && next !== value) onSave(next);
    else setDraft(String(value));
  };

  return (
    <div>
      <label className="mb-1 block text-xs text-ink-500">{label}</label>
      <input
        className="field w-full py-1.5 text-sm" type={type} step={step} value={draft} disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      />
    </div>
  );
}

const BRAND_LABELS: Record<string, string> = {
  app_name: "Appnamn", currency: "Valuta", locale: "Språk", time_zone: "Tidszon", vat_rate: "Moms (%)",
};

function Row({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-ink-300">{value || "—"}</dd>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: "ok" | "warning" | "danger" }) {
  const colors = {
    ok: "text-ink-200 border-ink-800",
    warning: "text-amber-300 border-amber-900/60",
    danger: "text-red-300 border-red-900/60",
  };
  return (
    <div className={`rounded-lg border bg-ink-850/40 p-3 ${colors[tone]}`}>
      <div className="text-xs text-ink-500">{label}</div>
      <div className="num mt-0.5 text-xl font-semibold">{number(value)}</div>
    </div>
  );
}

function Toggle({
  label, description, enabled, busy, onChange,
}: {
  label: string; description: string; enabled: boolean; busy: boolean; onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-ink-800 bg-ink-850/40 p-3">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-sm text-ink-200">
          {enabled && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
          {label}
        </div>
        <div className="text-xs text-ink-500">{description}</div>
      </div>
      <button
        role="switch"
        aria-checked={enabled}
        aria-label={label}
        disabled={busy}
        onClick={() => onChange(!enabled)}
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
          enabled ? "bg-emerald-500" : "bg-ink-700"
        }`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
            enabled ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}
